import { IconCheck, IconCopy, IconEye, IconFolderPlus, IconTrash, IconX } from '@tabler/icons'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { useDebounce } from '../../../utils'
import { Spinner } from '../../Spinner'
import { ReactComponent as AddFile } from '../assets/AddFile.svg'
import { useCreateFolder } from '../utils/index'
import Header from './Header'
import ImageCard from './ImageCard'

interface PropTypes {
  file: any
  siteId: number
  progress?: any
  status?: {
    uploaded: boolean
    loading: boolean
  }
  setStatus?: any
  mediaFolders?: Array<any>
  currentFolderId?: any
  onDrop?: any,
  done: ({ url, uid, fileName }: { url: string, uid?: string, fileName?: string }) => void,
  handleClose: () => void,
  error?: boolean,
  mediaType?: string,
  uid?: string,
  standalone?: boolean,
  onAttachmentsChanged?: () => void,
}

export interface FileTypes {
  id?: number
  name: string
  url: string
  large_url?: string
  type: string
  thumb_square_url?: string
  asset_file_name: string
  asset_content_type?: string,
  asset_file_size?: number
  created_at?: string
  media_folder_id?: number
}

const IconMoveFolder = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 14 14" fill="none">
      <g clip-path="url(#clip0_762_725)">
        <path d="M0.5 8.16675C1.63903 8.16675 6.41667 8.16675 6.41667 8.16675" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M4.66699 9.91675L6.41699 8.16675L4.66699 6.41675" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M1.75 6.41658V3.49992C1.75 3.1905 1.87292 2.89375 2.09171 2.67496C2.3105 2.45617 2.60725 2.33325 2.91667 2.33325H5.25L7 4.08325H11.0833C11.3928 4.08325 11.6895 4.20617 11.9083 4.42496C12.1271 4.64375 12.25 4.9405 12.25 5.24992V9.91658C12.25 10.226 12.1271 10.5228 11.9083 10.7415C11.6895 10.9603 11.3928 11.0833 11.0833 11.0833H1.74999" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
      </g>
      <defs>
        <clipPath id="clip0_762_725">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}

const Gallery = ({
  file,
  siteId,
  progress,
  status,
  setStatus,
  mediaFolders,
  currentFolderId,
  onDrop,
  done,
  handleClose,
  error,
  mediaType,
  uid,
  standalone = false,
  onAttachmentsChanged,
}: PropTypes) => {
  const [search, setSearch] = useState('')
  const [files, setFiles] = useState<Array<FileTypes>>([])
  const [newFiles, setNewFiles] = useState<Array<FileTypes>>([])
  const [selectedFiles, setSelectedFiles] = useState<Array<number>>([])
  const [createFolder] = useCreateFolder()

  const debouncedValues = useDebounce(search, 300)

  const handleDelete = async (id: any) => {
    const res = await axios.delete(`//${window.appDomain}/attachments/destroy_multiple?asset_type=${mediaType === 'document' ? 'pdf' : 'image'}&asset_name=${search}`, {
      data: {
        site_id: siteId,
        token: window.token,
        ids: id,
        ...(currentFolderId && { folder_id: currentFolderId })
      }
    });
    setFiles(res.data);
    setSelectedFiles([])
    onAttachmentsChanged?.()
  }

  const handleUpdateFolder = async (e: any, id: any) => {
    const res = await axios.post(`//${window.appDomain}/attachments/update_folder?asset_type=${mediaType === 'document' ? 'pdf' : 'image'}&asset_name=${search}`, {
      site_id: siteId,
      token: window.token,
      ids: id,
      ...(currentFolderId && { folder_id: currentFolderId }),
      ...(e.value && { move_folder_id: e.value }),
    });
    setFiles(res.data)
    setSelectedFiles([])
  }

  const moveOptions = mediaFolders?.length ? mediaFolders?.map((item) => {
    return {
      value: item.id,
      label: item.name
    }
  }).concat([{ value: null, label: 'Root' }])?.filter((item) => item.value !== currentFolderId) : [{ value: 'newFolder', label: 'New Folder', icon: <IconFolderPlus /> }];

  const deleteConfirmation = (item: any) => [{ value: 'confirm', label: 'Confirm', noHoverEffect: true, ctaIcon: <div className='flex'><span className='p-1 bg-primary-lighter cursor-pointer' onClick={(e) => handleDelete(item.id)}><IconCheck size={18} /></span><span className='p-1 bg-danger-lighter ml-1 cursor-pointer'><IconX size={18} /></span></div>, ctaIconClick: (e: any) => { } }]

  const imgOption = [
    {
      value: 'view',
      icon: <IconEye size="16px" className='text-[#0057FF] hover:text-[#001BCB]' />
    },
    ...(standalone
      ? [
          {
            value: 'copyUrl',
            icon: <IconCopy size="16px" className="text-[#0057FF] hover:text-[#001BCB]" stroke={1.5} />,
          },
        ]
      : []),
    {
      value: 'move',
      icon: <div className='text-[#0057FF] hover:text-[#001BCB]'><IconMoveFolder /></div>,
      callback: (e: any, id: any) => e.value === 'newFolder' ? createFolder(siteId, handleUpdateFolder, id) : handleUpdateFolder(e, id)
    },
    {
      value: 'delete',
      icon: <IconTrash size="16px" className='text-[#0057FF] hover:text-[#001BCB]' />,
      callback: (e: any) => { }
    }
  ]

  useEffect(() => {
    const getAttachments = async () => {
      setStatus((status: any) => ({ ...status, loading: true }))
      const attachmentResponse = await axios.get(`//${window.appDomain}/attachments.json?site_id=${siteId}${currentFolderId ? `&folder_id=${currentFolderId}` : ''}${search ? `&asset_name=${search}` : ''}&token=${window.token}&asset_type=${mediaType === 'document' ? 'pdf' : 'image'}`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      })
      if (attachmentResponse?.status == 200) {
        const filterNewFiles = newFiles.filter((item) => item?.media_folder_id === currentFolderId);
        setFiles([...filterNewFiles, ...attachmentResponse?.data])
        setStatus((status: any) => ({ ...status, loading: false }))
      } else {
        setStatus((status: any) => ({ ...status, loading: false }))
      }
    }
    getAttachments()
  }, [status?.uploaded, currentFolderId, debouncedValues, mediaType, siteId])

  useEffect(() => {
    if (Array.isArray(file) && file.length > 0) {
      let filterAssetName = newFiles.filter((item) => item?.asset_file_name !== file[0]);
      setNewFiles(filterAssetName)
      setFiles((prev) => prev.filter((item) => item?.asset_file_name !== file[0] && item?.name !== 'Uploading'))
    } else if (file) {
      let newFileObject = {
        name: 'Uploading',
        type: 'image/png',
        url: file?.url,
        asset_file_name: file?.fileName,
        media_folder_id: currentFolderId,
      }
      setFiles([newFileObject, ...files])
      setNewFiles([newFileObject, ...newFiles])
    }
  }, [file])

  const headerProps = {
    search,
    setSearch,
    files,
    selectedFiles,
    setSelectedFiles,
    handleDelete,
    handleUpdateFolder,
    moveOptions,
    currentFolderId,
    siteId
  }

  const imageCardProps = {
    selectedFiles,
    setSelectedFiles,
    done,
    uid,
    handleClose,
    files,
    imgOption,
    moveOptions,
    progress,
    deleteConfirmation,
    error,
    standalone,
  }

  return (
    <div className='h-[90%]'>
      <Header {...headerProps} />
      {
        status?.loading ? (
          <div className='h-[90%] bg-white border-2 border-dotted flex justify-center items-center'>
            <div className='flex flex-col justify-center items-center max-w-[300px] text-center'>
              <Spinner />
            </div>
          </div>
        ) : (
          files.length > 0 ?
            <div className='grid grid-cols-4 gap-4 max-h-[91%] overflow-y-auto overflow-x-hidden'>
              {
                files?.map((item, index) => <ImageCard key={item?.id} {...imageCardProps} item={item} index={index} />)
              }
            </div>
            :
            <div className='h-[90%] bg-white border-2 border-dotted flex justify-center items-center'>
              <div className='flex flex-col justify-center items-center max-w-[300px] text-center'>
                <AddFile />
                <div className='font-medium mt-4'>Add files</div>
                <div className='font-light'>
                  You don't have any files yet. Drag & drop or <label htmlFor='fileUpload' className='text-primary cursor-pointer'>choose file to upload</label>
                  <input id='fileUpload' type="file" className='hidden' multiple onChange={(e) => onDrop(e.target.files, [])} accept="image/*, application/pdf" />
                </div>
              </div>
            </div>
        )
      }
    </div>
  )
}

export default Gallery